import { useEffect, useState, useRef } from "react";
import api from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Plus, Pencil, Trash2, Upload, Loader2, Search } from "lucide-react";
import { ConfirmDialog } from "../../components/common";
import { toast } from "sonner";

export default function Brands() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", logo_url: "", is_active: true });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => { const { data } = await api.get("/brands"); setList(data); };
  useEffect(() => { load(); }, []);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("all");
  const [del, setDel] = useState(null);
  const shown = list.filter(b => (statusF === "all" || (statusF === "active") === (b.is_active !== false)) && (!q || b.name.toLowerCase().includes(q.toLowerCase())));

  const save = async () => {
    if (!form.name) { toast.error("Marka adı zorunlu"); return; }
    try {
      if (editId) await api.put(`/brands/${editId}`, form);
      else await api.post("/brands", form);
      toast.success("Kaydedildi");
      setOpen(false); setEditId(null); setForm({ name: "", logo_url: "", is_active: true });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };

  const remove = async () => {
    await api.delete(`/brands/${del.id}`);
    toast.success("Silindi"); setDel(null); load();
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      const token = localStorage.getItem("token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.path}?auth=${token}`;
      setForm({ ...form, logo_url: url });
      toast.success("Logo yüklendi");
    } catch { toast.error("Yükleme başarısız"); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-6" data-testid="brands-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Marka Kütüphanesi</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mt-1">Markalar</h1>
        </div>
        <Button onClick={() => { setEditId(null); setForm({ name: "", logo_url: "", is_active: true }); setOpen(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="new-brand-button">
          <Plus className="h-4 w-4 mr-2" /> Yeni Marka
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative w-full sm:w-64">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Marka ara" className="pl-9" data-testid="brand-search" />
        </div>
        {[["all", "Tümü"], ["active", "Aktif"], ["passive", "Pasif"]].map(([k, l]) => (
          <Button key={k} size="sm" variant={statusF === k ? "default" : "outline"} onClick={() => setStatusF(k)}>{l}</Button>
        ))}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {shown.map(b => (
          <Card key={b.id} className="p-4 border border-slate-200 shadow-none card-lift">
            <div className="aspect-[3/2] bg-white rounded-md border border-slate-200 flex items-center justify-center p-3">
              {b.logo_url ? (
                <img src={b.logo_url} alt={b.name} className="max-h-full max-w-full object-contain" onError={(e) => { e.target.style.display = "none"; }} />
              ) : (
                <div className="font-display font-bold text-2xl text-slate-700">{b.name}</div>
              )}
            </div>
            <div className="mt-3">
              <div className="font-semibold text-slate-900 text-sm">{b.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">{b.is_active ? "Aktif" : "Pasif"}</div>
            </div>
            <div className="mt-3 flex gap-1">
              <Button size="sm" variant="outline" className="flex-1 h-8" onClick={() => { setEditId(b.id); setForm({ name: b.name, logo_url: b.logo_url, is_active: b.is_active }); setOpen(true); }}>
                <Pencil className="h-3 w-3" />
              </Button>
              <Button size="sm" variant="outline" className="text-rose-600 h-8" onClick={() => setDel(b)} data-testid={`delete-brand-${b.id}`}><Trash2 className="h-3 w-3" /></Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? "Marka Düzenle" : "Yeni Marka"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Marka Adı *</Label><Input data-testid="brand-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div>
              <Label>Logo</Label>
              <div className="flex gap-2 mt-1.5">
                <Input placeholder="URL yapıştır veya yükle" value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} />
                <Button type="button" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="brand-logo-upload">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                </Button>
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleLogoUpload} />
              </div>
              <div className="text-xs text-slate-500 mt-1">Yüklenen logo PDF servis fişinde otomatik kullanılır.</div>
            </div>
            {form.logo_url && <img src={form.logo_url} alt="preview" className="h-16 object-contain bg-white border border-slate-200 rounded p-2" />}
            <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} /><Label>Aktif</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button onClick={save} className="bg-blue-600 hover:bg-blue-700">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={!!del} onOpenChange={(v) => !v && setDel(null)} title={`${del?.name} silinsin mi?`} description="Marka kalıcı olarak silinir." onConfirm={remove} />
    </div>
  );
}
